/* eslint-disable replo/no-tailwind-literal-color */
import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";

import { DeploymentStatusTag } from "@/features/react-editor/components/DeploymentStatusTag";
import Button from "@replo/design-system/components/button/Button";
import { MobileSheet } from "@replo/design-system/components/mobile-sheet/MobileSheet";
import Popover from "@replo/design-system/components/popover/Popover";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { ChevronDown } from "lucide-react";

import PageSelectionDropdown from "./PageSelectionDropdown";

type PageNameWithDropdownProps = {
  page: FileWithLatestFileVersion;
  projectId: string;
  isPageSelectionPopoverOpen: boolean;
  setIsPageSelectionPopoverOpen: (open: boolean) => void;
  onPageSelect: (page: FileWithLatestFileVersion) => void;
};

export default function PageNameWithDropdown({
  page,
  projectId,
  isPageSelectionPopoverOpen,
  setIsPageSelectionPopoverOpen,
  onPageSelect,
}: PageNameWithDropdownProps) {
  const logEvent = useLogAnalytics();
  const [isMobileDockOpen, setIsMobileDockOpen] = React.useState(false);

  return (
    <div className="flex items-center gap-2">
      <div className="hidden md:flex flex-row">
        <Popover
          onOpenChange={setIsPageSelectionPopoverOpen}
          hideCloseButton
          align="start"
          side="bottom"
          sideOffset={4}
          triggerAsChild
          content={
            <PageSelectionDropdown
              projectId={projectId}
              handlePageSelectionCardClick={onPageSelect}
              currentPageId={page.id}
            />
          }
          shouldPreventDefaultOnInteractOutside={false}
          className="w-[35vw] max-h-[75vh] overflow-y-auto pb-0 pr-0"
        >
          <Button
            variant="secondary"
            size="base"
            onClick={() => {
              logEvent("editor.nav.pages.click");
            }}
            endEnhancer={<ChevronDown size={16} />}
            UNSAFE_className={twMerge(
              "shadow-button-selectable",
              isPageSelectionPopoverOpen && "bg-surface",
            )}
          >
            <div className="flex flex-row items-center gap-1.5 mr-3">
              <Tooltip content={page.name} triggerAsChild>
                <div className="max-w-[150px] truncate">{page.name}</div>
              </Tooltip>
              <DeploymentStatusTag file={page} size="sm" />
            </div>
          </Button>
        </Popover>
      </div>

      <div className="md:hidden flex flex-row">
        <Button
          variant="secondary"
          size="base"
          endEnhancer={<ChevronDown size={16} />}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsMobileDockOpen(true);
          }}
          UNSAFE_className="shadow-button-selectable"
          layoutClassName="min-w-0"
        >
          <div className="flex flex-row items-center gap-1.5 mr-3 min-w-0">
            <Tooltip content={page.name} triggerAsChild>
              <div className="max-w-[150px] truncate">{page.name}</div>
            </Tooltip>
            <DeploymentStatusTag file={page} size="sm" />
          </div>
        </Button>

        <MobileSheet open={isMobileDockOpen} onOpenChange={setIsMobileDockOpen}>
          <div className="p-3">
            <PageSelectionDropdown
              projectId={projectId}
              handlePageSelectionCardClick={(selected) => {
                onPageSelect(selected);
                setIsMobileDockOpen(false);
              }}
              currentPageId={page.id}
            />
          </div>
        </MobileSheet>
      </div>
    </div>
  );
}
